import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Bio, education } from '../../data/constants';
import { Tilt } from 'react-tilt';
import { BlinkingCursor } from '../../utils/CommonStyles';

// Animation keyframes
const scanEffect = keyframes`
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 0%; }
  100% { background-position: 0% 0%; }
`;

const blinkEffect = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

// const typingEffect = keyframes`
//   from { width: 0 }
//   to { width: 100% }
// `;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Container styling
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 0;
  align-items: center;
  padding: 50px 0px 100px 0px;
  background: linear-gradient(
    343deg,
    rgba(19, 22, 30, 1) 0%,
    rgba(27, 30, 40, 1) 50%,
    rgba(19, 22, 30, 1) 100%
  );
  clip-path: polygon(0 0, 100% 0, 100% 100%, 70% 95%, 0 100%);
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  z-index: 0;
  max-width: 1100px;
  gap: 12px;
`;

const TitleSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const Title = styled.h2`
  font-size: 52px;
  text-align: center;
  font-weight: 700;
  margin-top: 20px;
  margin-bottom: 0;
  color: ${({ theme }) => theme.text_primary};
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 180px;
    height: 3px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.primary}, transparent);
  }

  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 36px;

    &:after {
      width: 120px;
    }
  }
`;

const Desc = styled.div`
  font-size: 18px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  margin-top: 25px;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

// Terminal window styling
const TerminalWindow = styled.div`
  width: 100%;
  max-width: 950px;
  margin-top: 20px;
  margin-bottom: 0;
  border-radius: 10px;
  background-color: #1a1a1a;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
  overflow: visible;
  border: 1px solid #333;
  position: relative;

  &:after {
    content: none;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      linear-gradient(to bottom, transparent 0%, rgba(0, 30, 60, 0.05) 100%),
      repeating-linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.1) 0px,
        rgba(0, 0, 0, 0.1) 1px,
        transparent 1px,
        transparent 2px
      );
    pointer-events: none;
    opacity: 0.3;
    z-index: 0;
    border: none;
  }
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(to bottom, transparent, rgba(120, 170, 255, 0.15), transparent);
  animation: ${scanEffect} 5s linear infinite;
  z-index: 10;
  pointer-events: none;
`;

const TerminalHeader = styled.div`
  display: flex;
  align-items: center;
  background-color: #262626;
  padding: 12px 20px;
  border-bottom: 1px solid #333;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
`;

const TerminalDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${props => props.color};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.2);
    box-shadow: 0 0 5px ${props => props.color};
  }
`;

const TerminalTitle = styled.div`
  flex: 1;
  text-align: center;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ddd;
  letter-spacing: 1px;
`;

const TerminalBody = styled.div`
  padding: 25px;
  font-family: 'Courier New', monospace;
  max-height: none;
  overflow-y: visible;
  position: relative;
  z-index: 1;

  &::-webkit-scrollbar {
    width: 0;
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;

  border-bottom: none;
  outline: none;
  box-shadow: none;
`;

const TerminalLine = styled.div`
  margin: 10px 0;
  line-height: 1.6;
  color: #ddd;
  display: flex;

  &:before {
    content: '$ ';
    color: ${({ theme }) => theme.primary};
    font-weight: bold;
    margin-right: 10px;
  }
`;

const CommandOutput = styled.div`
  margin: 10px 0 20px 25px;
  padding-left: 15px;
  border-left: 2px solid ${({ theme }) => theme.primary + '70'};
  position: relative;
  overflow: visible;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: 2px;
    height: 15px;
    background-color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }

  border-bottom: none;
`;

const StatusOutput = styled.div`
  margin: 10px 0 20px 25px;
  padding-left: 15px;
  border-left: 2px solid ${({ theme }) => theme.primary + '70'};
  position: relative;
  width: 100%;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -2px;
    width: 2px;
    height: 15px;
    background-color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }

  border-bottom: none;
`;

// Education card styling
const EducationCard = styled.div`
  position: relative;
  margin: 25px 0;
  padding: 20px;
  background: rgba(45, 45, 45, 0.5);
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transition: all 0.4s ease;
  border: 1px solid transparent;
  animation: ${fadeIn} 0.5s ease-out;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.primary + '50'},
      transparent
    );
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: ${({ theme }) => theme.primary + '50'};
    box-shadow:
      0 10px 20px rgba(0, 0, 0, 0.3),
      0 0 15px ${({ theme }) => theme.primary + '30'} inset;
    background-color: rgba(50, 50, 50, 0.9);

    &:before {
      transform: translateX(100%);
    }
  }

  @media (max-width: 768px) {
    margin: 15px 0;
  }
`;

const EducationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
`;

const SchoolLogo = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary + '20'};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${({ theme }) => theme.primary};
  overflow: hidden;
  transition: transform 0.3s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${EducationCard}:hover & {
    transform: scale(1.1);
  }
`;

const EducationInfo = styled.div`
  flex: 1;
`;

const Degree = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 5px;
  position: relative;
  display: inline-block;

  &:after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 0;
    height: 2px;
    background: ${({ theme }) => theme.primary};
    transition: width 0.3s ease;
  }

  ${EducationCard}:hover & {
    &:after {
      width: 100%;
    }
  }
`;

const School = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 4px;
`;

const Date = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
`;

const Description = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.5;
  margin-bottom: 15px;
  position: relative;
  padding-left: 15px;
  overflow: hidden;

  &:before {
    content: '>';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.primary};
    animation: ${blinkEffect} 1.5s infinite;
  }
`;

const Grade = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.primary + '15'};
  padding: 6px 12px;
  border-radius: 4px;
  display: inline-block;
  border: 1px solid ${({ theme }) => theme.primary + '30'};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    background: ${({ theme }) => theme.primary + '25'};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const Education = () => {
  const displayLimit = 4;
  const limitedEducation = education.slice(0, displayLimit);

  return (
    <Container id="Education">
      <Wrapper>
        <TitleSection>
          <Title>Education</Title>
          <Desc>My academic journey and achievements</Desc>
        </TitleSection>

        <TerminalWindow>
          <ScanLine />
          <TerminalHeader>
            <TerminalDot color="#ff5f56" />
            <TerminalDot color="#ffbd2e" />
            <TerminalDot color="#27c93f" />
            <TerminalTitle>
              {Bio.name.toLowerCase().replaceAll(' ', '')}@backend-dev ~ /education
            </TerminalTitle>
          </TerminalHeader>

          <TerminalBody>
            <TerminalLine>cat education.json</TerminalLine>
            <CommandOutput>
              {limitedEducation.map((edu, index) => (
                <Tilt key={index} options={{ max: 15, scale: 1.02, perspective: 1000 }}>
                  <EducationCard>
                    <EducationHeader>
                      <SchoolLogo>
                        <img src={edu.img} alt={edu.school} />
                      </SchoolLogo>
                      <EducationInfo>
                        <Degree>{edu.degree}</Degree>
                        <School>{edu.school}</School>
                        <Date>{edu.date}</Date>
                      </EducationInfo>
                    </EducationHeader>
                    <Description>{edu.desc}</Description>
                    {edu.grade && <Grade>Grade: {edu.grade}</Grade>}
                  </EducationCard>
                </Tilt>
              ))}
              {education.length > displayLimit && (
                <div style={{ textAlign: 'center', color: '#ddd', marginTop: '10px' }}>
                  ...more educational qualifications available on request
                </div>
              )}
            </CommandOutput>

            <TerminalLine>echo $EDUCATION_MOTTO</TerminalLine>
            <StatusOutput>
              <div style={{ color: '#ddd' }}>
                "Learning is a lifelong journey, not a destination"
              </div>
            </StatusOutput>

            <TerminalLine>
              <BlinkingCursor>_</BlinkingCursor>
            </TerminalLine>
          </TerminalBody>
        </TerminalWindow>
      </Wrapper>
    </Container>
  );
};

export default Education;
